// Write File Tool - Provides file writing functions for workspace
// Extends WorkspaceTool - has access to appType, toolSystem, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";
import { notifyWorkspaceServiceWorker } from "../../../../../../workspace/serviceWorkerBridge.js";

export class WriteFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'writeFileTool';
  }

  /**
   * Write file content
   * @param {string} path - Path to the file to write
   * @param {string} content - Content to write to the file
   * @param {boolean} [overwrite=true] - Whether to overwrite existing files
   * @param {string} [encoding='utf8'] - Content encoding (utf8 or base64)
   * @param {boolean} [createDirectories=true] - Create parent directories if needed
   * @param {string|null} [syncReason=null] - Reason for syncing open files (optional)
   * @returns {Promise<Object>} Result with operation status
   */
  async writeFile(
    path,
    content,
    overwrite = true,
    encoding = "utf8",
    createDirectories = true,
    syncReason = null
  ) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Validate content
      if (content === undefined || content === null) {
        return this.returnError({}, "Content is required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.writeFileForBrowser(
          path,
          content,
          overwrite,
          encoding,
          createDirectories,
          syncReason
        );
      }
      if (this.appType === "local") {
        return await this.writeFileForLocal(
          path,
          content,
          overwrite,
          encoding,
          createDirectories,
          syncReason
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for writeFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|writefile|error",
          color1: "red",
        },
        `Failed to write file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Write file for browser apps using Lightning FS
   */
  async writeFileForBrowser(
    path,
    content,
    overwrite,
    encoding,
    createDirectories,
    syncReason
  ) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path - Lightning FS uses absolute paths
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    // Check if file exists (for overwrite logic)
    let fileExists = false;
    try {
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        return this.returnError({}, `Path is a directory: ${path}`);
      }
      fileExists = true;
    } catch (error) {
      if (error.code !== "ENOENT") {
        throw error;
      }
      // File doesn't exist, which is fine for creation
    }

    // Check overwrite permission
    if (fileExists && !overwrite) {
      return this.returnError(
        {},
        `File already exists and overwrite is disabled: ${path}`
      );
    }

    // Ensure parent directory exists
    if (createDirectories) {
      const dirPath = filesystemPath.split("/").slice(0, -1).join("/") || "/";
      if (dirPath && dirPath !== "/") {
        await this.ensureDirectory(dirPath);
      }
    }

    // Prepare content for writing
    let dataToWrite;
    if (encoding === "base64") {
      // Decode base64 content to binary
      dataToWrite = this.decodeBase64(content);
    } else {
      // Write as UTF-8 string
      dataToWrite = content;
    }

    // Write the file
    await fs.writeFile(filesystemPath, dataToWrite);

    // Get file stats after write
    const stats = await fs.stat(filesystemPath);

    // Notify service worker that file was updated
    const appId = this.workspaceManager?.app?.id;
    if (appId) {
      try {
        await notifyWorkspaceServiceWorker({
          type: "WORKSPACE_FILE_UPDATED",
          appId: appId,
          filePath: normalizedPath,
        });
      } catch (error) {
        // Don't fail file write if notification fails
        logger.warn(
          {
            tags: "tools|writefile|notification",
            color1: "yellow",
          },
          "Failed to notify service worker of file update",
          error
        );
      }
    }

    // Sync changes with workspace manager to update UI
    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath], syncReason);
    }

    const action = fileExists ? "updated" : "created";
    logger.log(
      {
        tags: "tools|writefile|info",
        color1: "lime",
      },
      `File ${action}: ${filesystemPath}`,
      {
        size: stats.size,
        action,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath, // Return the original path without /files/ prefix
        action,
        size: stats.size,
      },
      `File ${action} successfully`
    );
  }

  /**
   * Simple write file operation - accepts path and content only
   * Creates new files or overwrites existing ones completely
   * @param {string} path - Path to the file to write (relative to workspace root)
   * @param {string} content - Content to write to the file (as string)
   * @returns {Promise<Object>} Result with operation status
   */
  async writeFileSimple(path, content) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Validate content
      if (content === undefined || content === null) {
        return this.returnError({}, "Content is required");
      }

      // Ensure content is a string
      if (typeof content !== "string") {
        return this.returnError({}, "Content must be a string");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.writeFileSimpleForBrowser(path, content);
      }
      if (this.appType === "local") {
        return await this.writeFileSimpleForLocal(path, content);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for writeFileSimple`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|writefile|simple|error",
          color1: "red",
        },
        `Failed to write file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Simple write file for browser apps using Lightning FS
   */
  async writeFileSimpleForBrowser(path, content) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize and validate path - ensure it's relative to workspace root
    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }

    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    // Check if path is a directory
    try {
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        return this.returnError({}, `Path is a directory: ${path}`);
      }
    } catch (error) {
      if (error.code !== "ENOENT") {
        throw error;
      }
      // File doesn't exist, which is fine for creation
    }

    // Ensure parent directory exists
    const dirPath = filesystemPath.split("/").slice(0, -1).join("/") || "/";
    if (dirPath && dirPath !== "/") {
      await this.ensureDirectory(dirPath);
    }

    // Write the file atomically (content is already a string, write as UTF-8)
    await fs.writeFile(filesystemPath, content);

    // Get file stats after write
    const stats = await fs.stat(filesystemPath);

    // Notify service worker that file was updated
    const appId = this.workspaceManager?.app?.id;
    if (appId) {
      try {
        await notifyWorkspaceServiceWorker({
          type: "WORKSPACE_FILE_UPDATED",
          appId: appId,
          filePath: normalizedPath,
        });
      } catch (error) {
        // Don't fail file write if notification fails
        logger.warn(
          {
            tags: "tools|writefile|simple|notification",
            color1: "yellow",
          },
          "Failed to notify service worker of file update",
          error
        );
      }
    }

    // Sync changes with workspace manager to update UI
    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    logger.log(
      {
        tags: "tools|writefile|simple|info",
        color1: "lime",
      },
      `File written: ${filesystemPath}`,
      {
        size: stats.size,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        size: stats.size,
      },
      "File written successfully"
    );
  }

  /**
   * Absolute working directory for local apps (from Mongo app.settings.workingDirectoryPath).
   * @returns {string|null}
   */
  /**
   * Full writeFile for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async writeFileForLocal(
    path,
    content,
    overwrite,
    encoding,
    createDirectories,
    syncReason
  ) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.writeFile) {
      return this.returnError(
        {},
        "Local file writes require Revolt Desktop (workspaceLocalAppAPI.writeFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    let fileExists = false;
    let isDirectory = false;
    if (api.getPathInfo) {
      const info = await api.getPathInfo({
        rootPath: root,
        relativePath,
      });
      if (!info?.success) {
        return this.returnError(
          {},
          info?.error || "Failed to inspect path on disk"
        );
      }
      fileExists = !!info.exists;
      isDirectory = !!info.isDirectory;
    }

    if (isDirectory) {
      return this.returnError({}, `Path is a directory: ${path}`);
    }
    if (fileExists && !overwrite) {
      return this.returnError(
        {},
        `File already exists and overwrite is disabled: ${path}`
      );
    }

    if (createDirectories === false) {
      const parentRel = relativePath.includes("/")
        ? relativePath.replace(/\/[^/]+$/, "")
        : "";
      if (parentRel && api.getPathInfo) {
        const parentInfo = await api.getPathInfo({
          rootPath: root,
          relativePath: parentRel,
        });
        if (!parentInfo?.success || !parentInfo.exists) {
          return this.returnError(
            {},
            "Parent directory does not exist (createDirectories is false)"
          );
        }
      }
    }

    let contentPayload = content;
    let enc = "utf8";
    if (encoding === "base64") {
      enc = "base64";
      if (typeof content !== "string") {
        return this.returnError({}, "base64 content must be a string");
      }
      contentPayload = content;
    } else if (typeof content === "string") {
      contentPayload = content;
    } else {
      return this.returnError({}, "Content must be a string for utf8 encoding");
    }

    const res = await api.writeFile({
      rootPath: root,
      relativePath,
      content: contentPayload,
      encoding: enc,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to write file on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath], syncReason);
    }

    const action = fileExists ? "updated" : "created";
    return this.returnSuccess(
      {
        path: normalizedPath,
        action,
        size: res.size ?? null,
      },
      `File ${action} successfully`
    );
  }

  /**
   * Simple UTF-8 write for local apps (used by AI write-file tool via writeFileSimple).
   */
  async writeFileSimpleForLocal(path, content) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.writeFile) {
      return this.returnError(
        {},
        "Local file writes require Revolt Desktop (workspaceLocalAppAPI.writeFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    if (api.getPathInfo) {
      const info = await api.getPathInfo({
        rootPath: root,
        relativePath,
      });
      if (!info?.success) {
        return this.returnError(
          {},
          info?.error || "Failed to inspect path on disk"
        );
      }
      if (info.exists && info.isDirectory) {
        return this.returnError({}, `Path is a directory: ${path}`);
      }
    }

    const res = await api.writeFile({
      rootPath: root,
      relativePath,
      content,
      encoding: "utf8",
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to write file on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    return this.returnSuccess(
      {
        path: normalizedPath,
        size: res.size ?? null,
      },
      "File written successfully"
    );
  }

  /**
   * Search and replace text in a file
   * Performs exact string matching and replacement atomically
   * @param {string} path - Path to the file (relative to workspace root)
   * @param {string} oldString - String to search for (must exist in file)
   * @param {string} newString - String to replace with
   * @param {boolean} [replaceAll=false] - Whether to replace all occurrences
   * @returns {Promise<Object>} Result with operation status
   */
  async searchReplace(path, oldString, newString, replaceAll = false) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      // Validate oldString
      if (
        oldString === undefined ||
        oldString === null ||
        typeof oldString !== "string" ||
        oldString.length === 0
      ) {
        return this.returnError(
          {},
          "old_string is required and must be a non-empty string"
        );
      }

      // Validate newString (can be empty string, but must be defined)
      if (
        newString === undefined ||
        newString === null ||
        typeof newString !== "string"
      ) {
        return this.returnError(
          {},
          "new_string is required and must be a string"
        );
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.searchReplaceForBrowser(
          path,
          oldString,
          newString,
          replaceAll
        );
      }
      if (this.appType === "local") {
        return await this.searchReplaceForLocal(
          path,
          oldString,
          newString,
          replaceAll
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for searchReplace`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|writefile|searchreplace|error",
          color1: "red",
        },
        `Failed to search-replace in file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Search and replace for browser apps using Lightning FS
   */
  async searchReplaceForBrowser(path, oldString, newString, replaceAll) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize and validate path - ensure it's relative to workspace root
    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }

    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    // Check if file exists
    let fileContent;
    try {
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        return this.returnError({}, `Path is a directory: ${path}`);
      }

      // Read file content
      const buffer = await fs.readFile(filesystemPath);
      fileContent = this.decodeUint8(buffer);
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `File not found: ${path}`);
      }
      throw error;
    }

    // Validate that old_string exists in the file
    if (!fileContent.includes(oldString)) {
      return this.returnError(
        {},
        `The string to replace was not found in the file: ${path}`
      );
    }

    // Perform replacement
    let newContent;
    let replacementCount;
    if (replaceAll) {
      // Replace all occurrences
      const regex = new RegExp(this.escapeRegex(oldString), "g");
      newContent = fileContent.replace(regex, newString);
      // Count occurrences
      const matches = fileContent.match(
        new RegExp(this.escapeRegex(oldString), "g")
      );
      replacementCount = matches ? matches.length : 0;
    } else {
      // Replace first occurrence only
      newContent = fileContent.replace(oldString, newString);
      replacementCount = 1;
    }

    // Write the file back atomically
    await fs.writeFile(filesystemPath, newContent);

    // Get file stats after write
    const stats = await fs.stat(filesystemPath);

    // Notify service worker that file was updated
    const appId = this.workspaceManager?.app?.id;
    if (appId) {
      try {
        await notifyWorkspaceServiceWorker({
          type: "WORKSPACE_FILE_UPDATED",
          appId: appId,
          filePath: normalizedPath,
        });
      } catch (error) {
        // Don't fail file write if notification fails
        logger.warn(
          {
            tags: "tools|writefile|searchreplace|notification",
            color1: "yellow",
          },
          "Failed to notify service worker of file update",
          error
        );
      }
    }

    // Sync changes with workspace manager to update UI
    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    logger.log(
      {
        tags: "tools|writefile|searchreplace|info",
        color1: "lime",
      },
      `Search-replace completed in file: ${filesystemPath}`,
      {
        replacementCount,
        replaceAll,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        replacementCount,
        replaceAll,
        size: stats.size,
      },
      `Successfully replaced ${replacementCount} occurrence(s) in file`
    );
  }

  /**
   * Search and replace for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async searchReplaceForLocal(path, oldString, newString, replaceAll) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.searchReplace) {
      return this.returnError(
        {},
        "Local search-replace requires Revolt Desktop (workspaceLocalAppAPI.searchReplace)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");
    if (!relativePath) {
      return this.returnError({}, "Invalid file path");
    }

    const res = await api.searchReplace({
      rootPath: root,
      relativePath,
      oldString,
      newString,
      replaceAll,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to search-replace on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    logger.log(
      {
        tags: "tools|writefile|searchreplace|info",
        color1: "lime",
      },
      `Search-replace completed (local): ${normalizedPath}`,
      {
        replacementCount: res.replacementCount,
        replaceAll,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        replacementCount: res.replacementCount,
        replaceAll,
        size: res.size ?? null,
      },
      `Successfully replaced ${res.replacementCount} occurrence(s) in file`
    );
  }

  /**
   * Ensure directory exists, creating parent directories as needed
   */
  async ensureDirectory(directoryPath) {
    if (!directoryPath || directoryPath === "/") {
      return;
    }

    const fs = this.fs?.promises;
    if (!fs) {
      return;
    }

    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            // Directory might have been created in parallel; ignore
            if (mkdirError.code !== "EEXIST") {
              logger.warn(
                {
                  tags: "tools|writefile|mkdir",
                  color1: "yellow",
                },
                `Failed to create directory: ${currentPath}`,
                mkdirError
              );
            }
          }
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * Decode base64 string to Uint8Array
   */
  decodeBase64(base64String) {
    if (typeof atob === "function") {
      const binaryString = atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
    if (typeof Buffer !== "undefined") {
      return Uint8Array.from(Buffer.from(base64String, "base64"));
    }
    throw new Error("Unable to decode base64 content");
  }

  /**
   * Decode Uint8Array buffer to UTF-8 string
   */
  decodeUint8(buffer) {
    if (typeof TextDecoder !== "undefined") {
      try {
        const decoder = new TextDecoder("utf-8");
        return decoder.decode(buffer);
      } catch (error) {
        logger.warn(
          {
            tags: "tools|writefile|decode",
            color1: "yellow",
          },
          "Failed to decode file as UTF-8",
          error
        );
      }
    }
    try {
      return Array.from(buffer || [])
        .map((byte) => String.fromCharCode(byte))
        .join("");
    } catch (error) {
      logger.warn(
        {
          tags: "tools|writefile|decode",
          color1: "yellow",
        },
        "Failed to decode file buffer",
        error
      );
      return "";
    }
  }

  /**
   * Escape special regex characters in a string for use in RegExp
   */
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
